import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, uploadToPresignedUrl } from '../api';
import { formatDate, Page, State } from '../components';
import { useAuth } from '../auth';

interface Agent {
  id: string;
  displayName: string;
}
interface Document {
  id: string;
  agentId: string;
  originalFilename: string;
  byteSize: number;
  status: string;
  processingError?: string;
  createdAt: string;
}

export function KnowledgePage() {
  const client = useQueryClient();
  const { user } = useAuth();
  const canRecover = user?.role === 'OWNER' || user?.role === 'ADMIN';
  const [agentId, setAgentId] = useState('');
  const [file, setFile] = useState<File>();
  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: () => api<{ data: Agent[] }>('/api/v1/agents'),
  });
  const recovery = useQuery({
    queryKey: ['knowledge-recovery', user?.tenantId],
    enabled: canRecover,
    queryFn: () => api('/api/v1/knowledge/recover', { method: 'POST' }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  });
  const docs = useQuery({
    queryKey: ['knowledge'],
    queryFn: () => api<{ data: Document[] }>('/api/v1/knowledge'),
    enabled: !canRecover || recovery.isFetched,
    refetchInterval: (query) =>
      query.state.data?.data.some((doc) => ['QUEUED', 'PROCESSING'].includes(doc.status))
        ? 5000
        : false,
  });
  const upload = useMutation({
    mutationFn: async () => {
      if (!file || !agentId) throw new Error('Choose an agent and file');
      const request = await api<{
        document: Document;
        upload: { url: string; headers: Record<string, string> };
      }>('/api/v1/knowledge/uploads', {
        method: 'POST',
        body: JSON.stringify({
          agentId,
          filename: file.name,
          mimeType: file.type,
          byteSize: file.size,
        }),
      });
      await uploadToPresignedUrl(request.upload.url, file);
      await api(`/api/v1/knowledge/${request.document.id}/confirm`, { method: 'POST' });
    },
    onSuccess: () => {
      setFile(undefined);
      void client.invalidateQueries({ queryKey: ['knowledge'] });
    },
  });
  const retry = useMutation({
    mutationFn: (id: string) => api(`/api/v1/knowledge/${id}/retry`, { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['knowledge'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/knowledge/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['knowledge'] }),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    upload.mutate();
  };
  return (
    <Page
      title="Knowledge"
      description="Files upload directly to private R2, then process into this agent’s vector store."
    >
      <form className="card upload-bar" onSubmit={submit}>
        <label>
          Agent
          <select required value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">Select agent…</option>
            {agents.data?.data.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Document
          <input
            type="file"
            accept=".pdf,.txt,.md,.docx"
            required
            onChange={(e) => setFile(e.target.files?.[0])}
          />
        </label>
        <button className="primary" disabled={upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Upload'}
        </button>
        {upload.error && <p className="form-error">{upload.error.message}</p>}
      </form>
      <State loading={docs.isLoading} error={docs.error} empty={docs.data?.data.length === 0}>
        {(retry.isError || remove.isError) && (
          <p className="mutation-error" role="alert">
            Could not {retry.isError ? 'retry' : 'delete'} this document:{' '}
            {
              docs.data?.data.find(
                (doc) => doc.id === (retry.isError ? retry.variables : remove.variables),
              )?.originalFilename
            }
            . Select {retry.isError ? 'Retry' : 'Delete'} to try again.
          </p>
        )}
        <div className="card table-scroll">
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th>Status</th>
                <th>Size</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {docs.data?.data.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <strong>{doc.originalFilename}</strong>
                    {doc.processingError && (
                      <small className="row-error">{doc.processingError}</small>
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge ${doc.status === 'READY' ? 'ready' : doc.status === 'FAILED' ? 'failed' : ''}`}
                    >
                      {doc.status}
                    </span>
                  </td>
                  <td>{Math.ceil(doc.byteSize / 1024)} KB</td>
                  <td>{formatDate(doc.createdAt)}</td>
                  <td>
                    <div className="row-actions">
                      {doc.status === 'FAILED' && (
                        <button
                          disabled={retry.isPending || remove.isPending}
                          onClick={() => {
                            remove.reset();
                            retry.mutate(doc.id);
                          }}
                        >
                          {retry.isPending && retry.variables === doc.id ? 'Retrying...' : 'Retry'}
                        </button>
                      )}
                      <button
                        className="danger ghost"
                        disabled={retry.isPending || remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete ${doc.originalFilename} from R2 and OpenAI knowledge?`,
                            )
                          ) {
                            retry.reset();
                            remove.mutate(doc.id);
                          }
                        }}
                      >
                        {remove.isPending && remove.variables === doc.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                    {((retry.isPending && retry.variables === doc.id) ||
                      (remove.isPending && remove.variables === doc.id)) && (
                      <p className="mutation-status" role="status">
                        Updating this document...
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </State>
    </Page>
  );
}
